import React, { useEffect, useRef, useState } from 'react';
import apiService from '../services/ApiService';

/**
 * Chat assistente per il triage diagnostico. Stesso pattern dell'app tools:
 * SSE da /api/admin/diagnose/chat, eventi tipizzati (text/tool_start/
 * tool_result/error/done), conversation history nello state locale.
 */
type Role = 'user' | 'assistant';
type ToolStatus = 'running' | 'ok' | 'error';
interface ToolCall { id: string; name: string; status: ToolStatus; }
interface Message {
    id: string; role: Role; text: string;
    toolCalls?: ToolCall[]; isError?: boolean;
}

const SUGGESTIONS = [
    'Riassumi la salute del tool nelle ultime 24h',
    'Mostra gli ultimi 10 errori',
    'Quali segnalazioni utente sono ancora aperte?',
    'Trova il pattern di errore più frequente',
];

const DiagnoseAssistantTab: React.FC<{ toolCode: string }> = ({ toolCode }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        setMessages([]); setInput('');
        if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
        setStreaming(false);
    }, [toolCode]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages]);

    const send = async (text: string) => {
        const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', text };
        const assistantMsg: Message = { id: `a-${Date.now()}`, role: 'assistant', text: '', toolCalls: [] };
        const historyForBe = messages.map(m => ({ role: m.role, content: m.text }));
        setMessages(prev => [...prev, userMsg, assistantMsg]);
        setStreaming(true);

        const ctrl = new AbortController();
        abortRef.current = ctrl;

        try {
            const stream = await apiService.diagnoseChatStream(
                { toolCode, messages: [...historyForBe, { role: 'user', content: text }] },
                ctrl.signal,
            );
            for await (const ev of parseSseStream(stream)) {
                if (ev.event === 'text') {
                    const delta = (ev.data?.text ?? '') as string;
                    setMessages(prev => updateLast(prev, m => ({ ...m, text: m.text + delta })));
                } else if (ev.event === 'tool_start') {
                    const tc: ToolCall = { id: ev.data?.id, name: ev.data?.name, status: 'running' };
                    setMessages(prev => updateLast(prev, m => ({
                        ...m, toolCalls: [...(m.toolCalls ?? []), tc],
                    })));
                } else if (ev.event === 'tool_result') {
                    const id = ev.data?.id as string;
                    const isErr = !!ev.data?.is_error;
                    setMessages(prev => updateLast(prev, m => ({
                        ...m,
                        toolCalls: (m.toolCalls ?? []).map(t =>
                            t.id === id ? { ...t, status: isErr ? 'error' : 'ok' } : t),
                    })));
                } else if (ev.event === 'error') {
                    const msg = (ev.data?.message ?? 'Errore') as string;
                    setMessages(prev => updateLast(prev, m => ({
                        ...m, text: (m.text ? m.text + '\n\n' : '') + `⚠️ ${msg}`, isError: true,
                    })));
                }
            }
        } catch (err: any) {
            if (err.name === 'AbortError') return;
            setMessages(prev => updateLast(prev, m => ({
                ...m, text: (m.text ? m.text + '\n\n' : '') + `⚠️ ${err.message}`, isError: true,
            })));
        } finally {
            setStreaming(false);
            abortRef.current = null;
        }
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const t = input.trim();
        if (!t || streaming) return;
        setInput('');
        void send(t);
    };

    return (
        <div className="d-flex flex-column gap-3" style={{ height: 'calc(100vh - 320px)', minHeight: 400 }}>
            <div className="small text-body-secondary">
                Assistente AI per il triage di <code>{toolCode}</code>. Accesso ai 6 tool diagnostici read-only.
                Sessione non persistita — al refresh la conversazione si perde.
            </div>

            <div ref={scrollRef} className="flex-grow-1 overflow-auto p-3 bg-body-tertiary rounded border d-flex flex-column gap-2">
                {messages.length === 0 && (
                    <div className="d-flex flex-column gap-2 py-2">
                        <div className="small text-body-secondary">Esempi:</div>
                        {SUGGESTIONS.map(s => (
                            <button key={s} type="button"
                                onClick={() => setInput(s)}
                                className="btn btn-outline-secondary btn-sm text-start">
                                {s}
                            </button>
                        ))}
                    </div>
                )}
                {messages.map(m => <Bubble key={m.id} msg={m} />)}
                {streaming && <div className="small text-body-secondary fst-italic">Sto pensando…</div>}
            </div>

            <form onSubmit={onSubmit} className="d-flex gap-2">
                <input type="text" className="form-control"
                    placeholder="Chiedi qualcosa sul tool…"
                    value={input} onChange={e => setInput(e.target.value)}
                    disabled={streaming} />
                {streaming ? (
                    <button type="button" className="btn btn-danger" onClick={() => abortRef.current?.abort()}>
                        Stop
                    </button>
                ) : (
                    <button type="submit" className="btn btn-primary" disabled={!input.trim()}>
                        Invia
                    </button>
                )}
            </form>
        </div>
    );
};

const Bubble: React.FC<{ msg: Message }> = ({ msg }) => {
    const isUser = msg.role === 'user';
    return (
        <div className={`d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'}`}>
            <div className={`p-2 rounded ${isUser ? 'bg-primary text-white' : msg.isError ? 'bg-danger-subtle' : 'bg-body'}`}
                style={{ maxWidth: '85%', whiteSpace: 'pre-wrap', border: msg.isError ? undefined : '1px solid var(--bs-border-color)' }}>
                {msg.text}
                {msg.toolCalls && msg.toolCalls.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                        {msg.toolCalls.map(t => (
                            <ToolPill key={t.id} call={t} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const ToolPill: React.FC<{ call: ToolCall }> = ({ call }) => {
    const cls = call.status === 'running' ? 'bg-warning text-dark'
        : call.status === 'ok' ? 'bg-success' : 'bg-danger';
    const icon = call.status === 'running' ? 'bi-arrow-clockwise'
        : call.status === 'ok' ? 'bi-check' : 'bi-exclamation-triangle';
    return (
        <span className={`badge ${cls}`}>
            <i className={`bi ${icon} me-1`} />
            <code className="text-white">{call.name}</code>
        </span>
    );
};

function updateLast(messages: Message[], updater: (m: Message) => Message): Message[] {
    if (messages.length === 0 || messages[messages.length - 1].role !== 'assistant') return messages;
    return [...messages.slice(0, -1), updater(messages[messages.length - 1])];
}

async function* parseSseStream(
    stream: ReadableStream<Uint8Array>,
): AsyncGenerator<{ event: string; data: any }, void, unknown> {
    const reader = stream.getReader();
    const decoder = new TextDecoder('utf-8');
    let buf = '';
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf('\n\n')) >= 0) {
            const frame = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let event = 'message';
            const dataLines: string[] = [];
            for (const line of frame.split('\n')) {
                if (line.startsWith('event:')) event = line.slice(6).trim();
                else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
            }
            if (dataLines.length === 0) continue;
            try { yield { event, data: JSON.parse(dataLines.join('\n')) }; }
            catch { /* skip */ }
        }
    }
}

export default DiagnoseAssistantTab;
